import { useMemo, useState } from 'react'
import type { PageQuery } from './SearchTable'
import { isEmptySortValue } from '../order/sortValue'

export type SortQuery = Pick<PageQuery, 'sortBy' | 'sortDirection'>
export type SortValue = string | number | boolean | null | undefined
export type SortValues<T> = Record<string, (row: T) => SortValue>

export function initialSort(values?: SortValues<any>): SortQuery {
  return { sortBy: Object.keys(values || {})[0], sortDirection: 'asc' }
}

export function sortRows<T>(data: T[], query: SortQuery, values?: SortValues<T>): T[] {
  const value = query.sortBy && values?.[query.sortBy]
  if (!value) return data
  return [...data].sort((a, b) => {
    const left = value(a), right = value(b)
    const leftEmpty = isEmptySortValue(left), rightEmpty = isEmptySortValue(right)
    if (leftEmpty || rightEmpty) return leftEmpty === rightEmpty ? 0 : leftEmpty ? 1 : -1
    if (left == null || right == null) return 0
    const result = typeof left === 'string' && typeof right === 'string'
      ? left.localeCompare(right, undefined, { sensitivity: 'base', numeric: true })
      : left < right ? -1 : left > right ? 1 : 0
    return query.sortDirection === 'desc' ? -result : result
  })
}

export function useTableSort<T>(data: T[], values: SortValues<T>) {
  const [query, setQuery] = useState<SortQuery>(() => initialSort(values))
  const rows = useMemo(() => sortRows(data, query, values), [data, query, values])
  const onSort = (field: string) => {
    if (!values[field]) return
    setQuery(q => ({ sortBy: field, sortDirection: q.sortBy === field && q.sortDirection === 'asc' ? 'desc' : 'asc' }))
  }
  return { rows, query, onSort }
}
